import { useEffect, useMemo, useState } from 'react';
import { usePublicClient, useWalletClient } from 'wagmi';
import { useAppKitAccount } from '@reown/appkit/react';
import { environment } from '@/config/environment';
import { TOKEN_LOCK_ABI } from '@/contracts/TokenLock.abi';
import { TOKEN_LOCK_FACTORY_ABI } from '@/contracts/TokenLockFactory.abi';
import { parseTxError } from '@/utils/txError';
import type { LockInfo, VestingSchedule } from './useWeb3';
import type { Address } from 'viem';
import {
  LEGACY_LOCK_LOOKUP_ABI,
  payableCeiling,
  readFactoryOwnership,
  readLockState,
} from '@/contracts/readState';

// Contract duration constants (in seconds), plus TRANCHE_COUNT, which is a plain
// count rather than a duration but is read from the same immutable constants.
interface ContractDurations {
  cliffDuration: bigint;
  vestingDuration: bigint;
  monthlyDuration: bigint;
  trancheCount: bigint;
}

const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000';

export function useTokenLock() {
  const { address, isConnected } = useAppKitAccount();
  const publicClient = usePublicClient();
  const { data: walletClient } = useWalletClient();

  /**
   * Every grant this wallet holds. One address may now hold any number of them
   * -- an investor in two rounds gets two, each with its own name, schedule,
   * funding and cancellation -- so this is the shape everything downstream is
   * built on. `locks` and `lockAddresses` are INDEX-ALIGNED with each other and
   * with `lockNames`; nothing else relates them.
   */
  const [locks, setLocks] = useState<LockInfo[]>([]);
  const [lockAddresses, setLockAddresses] = useState<Address[]>([]);
  const [lockNames, setLockNames] = useState<string[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  // isLoading is also set by the background getLockState poll, so a claim in
  // flight needs its own flag for anything that reports transaction state.
  const [isClaiming, setIsClaiming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isOwner, setIsOwner] = useState(false);
  // True when this wallet is the factory's pendingOwner: it owns nothing yet, it
  // only has an acceptOwnership call waiting for it.
  const [isPendingOwner, setIsPendingOwner] = useState(false);
  // Both halves of the first read have to settle before a route may be decided;
  // see rolesLoaded below.
  const [rolesSettled, setRolesSettled] = useState(false);
  const [locksSettled, setLocksSettled] = useState(false);
  const [incrementalFunding, setIncrementalFunding] = useState<boolean | null>(null);
  const [isPaused, setIsPaused] = useState(false);
  const [durations, setDurations] = useState<ContractDurations | null>(null);
  const [tgeTime, setTgeTime] = useState(0n);

  // Informational chart anchor. A failed read must not block ownership or claims.
  useEffect(() => {
    setTgeTime(0n);
    if (!publicClient || !isConnected) return;
    let cancelled = false;
    const readTge = async () => {
      try {
        const value = await publicClient.readContract({
          address: environment.factoryAddress as Address,
          abi: TOKEN_LOCK_FACTORY_ABI,
          functionName: 'tgeTime',
        });
        if (!cancelled) setTgeTime(value);
      } catch (err) {
        if (!cancelled) console.error('Error fetching TGE:', err);
      }
    };
    void readTge();
    const interval = setInterval(readTge, 30_000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [publicClient, isConnected]);

  /**
   * True once the first factory read for this account has settled, either way.
   * Route guards must wait for it: isEligible, isOwner and isPendingOwner all
   * start false and are reset to false on every account change, so a guard that
   * reads them any earlier bounces a real owner off their own deep link.
   */
  const rolesLoaded = rolesSettled && locksSettled;

  /** A wallet is eligible when it holds at least one grant. */
  const isEligible = lockAddresses.length > 0;

  // Content key for the address list. The poll rebuilds the array every 30
  // seconds, so effects must depend on what is IN it, not on its identity.
  const lockKey = lockAddresses.join(',');

  // ------------------------------------------------------------------
  // Factory roles. Deliberately its OWN read, not bundled with the
  // beneficiary lookup.
  //
  // These two used to share one Promise.all, which meant a single failed
  // eligibility read -- a missing selector on an older factory, one flaky RPC
  // response -- rejected the whole batch and left isOwner and isPendingOwner
  // false. Those two gate the admin panel, so a beneficiary-side problem took
  // the ADMIN side down with it, in precisely the situation where an operator
  // needs the admin panel to fix things. They are independent questions about
  // independent contracts and they now fail independently.
  // ------------------------------------------------------------------
  useEffect(() => {
    if (!isConnected || !address || !publicClient) {
      setIsOwner(false);
      setIsPendingOwner(false);
      setIncrementalFunding(null);
      setIsPaused(false);
      setRolesSettled(true);
      return;
    }

    let cancelled = false;
    let fetching = false;
    setIsOwner(false);
    setIsPendingOwner(false);
    setIncrementalFunding(null);
    setRolesSettled(false);

    const fetchRoles = async (initial = false) => {
      if (fetching) return;
      fetching = true;
      try {
        const factoryAddress = environment.factoryAddress as Address;
        const [ownership, paused] = await Promise.all([
          readFactoryOwnership(publicClient, factoryAddress),
          publicClient.readContract({
            address: factoryAddress,
            abi: TOKEN_LOCK_FACTORY_ABI,
            functionName: 'paused',
          }) as Promise<boolean>,
        ]);
        if (cancelled) return;
        setIncrementalFunding(ownership.incrementalFunding);
        setIsOwner(ownership.owner.toLowerCase() === address.toLowerCase());
        setIsPendingOwner(ownership.pendingOwner.toLowerCase() === address.toLowerCase());
        setIsPaused(paused);
      } catch (err) {
        if (!cancelled) console.error('Error fetching factory roles:', err);
      } finally {
        fetching = false;
        if (!cancelled && initial) setRolesSettled(true);
      }
    };

    fetchRoles(true);
    // A nomination can arrive while this wallet is already connected, so roles
    // are polled even for an account that holds no grant at all.
    const interval = setInterval(() => fetchRoles(), 30000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [isConnected, address, publicClient]);

  // ------------------------------------------------------------------
  // Which grants this wallet holds.
  // ------------------------------------------------------------------
  useEffect(() => {
    if (!isConnected || !address || !publicClient) {
      setLockAddresses([]);
      setLockNames([]);
      setLocks([]);
      setIsInitialized(false);
      setIsLoading(false);
      setLocksSettled(true);
      return;
    }

    let cancelled = false;
    let fetching = false;
    setLockAddresses([]);
    setLockNames([]);
    setLocks([]);
    setIsInitialized(false);
    setLocksSettled(false);

    const fetchLockAddresses = async (initial = false) => {
      if (fetching) return;
      fetching = true;
      if (initial) setIsLoading(true);
      try {
        const factoryAddress = environment.factoryAddress as Address;
        let found: Address[];
        let names: string[];

        if (environment.factoryAbiVersion === 'v10') {
          found = [...(await publicClient.readContract({
            address: factoryAddress,
            abi: TOKEN_LOCK_FACTORY_ABI,
            functionName: 'getLockContracts',
            args: [address as Address],
          }) as readonly Address[])];
          // One round trip for every label, rather than one per grant.
          names = found.length === 0 ? [] : [...(await publicClient.readContract({
            address: factoryAddress,
            abi: TOKEN_LOCK_FACTORY_ABI,
            functionName: 'getLockNames',
            args: [found],
          }) as readonly string[])];
        } else {
          // v7/v9 had exactly one lock per beneficiary and no names at all.
          // getLockContract is gone from the regenerated ABI, so the selector
          // is kept in readState.ts alongside the frozen getLockState tuples.
          const lock = await publicClient.readContract({
            address: factoryAddress,
            abi: LEGACY_LOCK_LOOKUP_ABI,
            functionName: 'getLockContract',
            args: [address as Address],
          }) as Address;
          found = lock === ZERO_ADDRESS ? [] : [lock];
          names = found.map(() => '');
        }

        if (cancelled) return;
        // Only replace the array when its CONTENTS changed. Handing out a fresh
        // identity every 30 seconds would restart every effect keyed on it.
        setLockAddresses((prev) => (prev.join(',') === found.join(',') ? prev : found));
        // Escaped, never a literal NUL byte in the source: one raw 0x00 makes grep
        // classify this whole file as binary and skip it silently, which is how an
        // earlier audit sweep of dapp/src missed this hook entirely.
        setLockNames((prev) =>
          prev.join('\u0000') === names.join('\u0000') ? prev : names,
        );
        setError(null);
      } catch (err) {
        if (!cancelled) {
          console.error('Error fetching lock addresses:', err);
          setError('Failed to fetch lock information');
        }
      } finally {
        fetching = false;
        if (!cancelled && initial) {
          setIsLoading(false);
          setLocksSettled(true);
        }
      }
    };

    fetchLockAddresses(true);
    const interval = setInterval(() => fetchLockAddresses(), 30000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [isConnected, address, publicClient]);

  // ------------------------------------------------------------------
  // Duration constants. Read ONCE, from whichever grant happens to be first:
  // CLIFF_DURATION, VESTING_DURATION, MONTHLY_DURATION and TRANCHE_COUNT are
  // `constant`, identical in every TokenLock this factory ever deploys, so
  // reading them per grant is four RPC calls multiplied by nothing.
  // ------------------------------------------------------------------
  const firstLock = lockAddresses[0] ?? null;
  useEffect(() => {
    if (!firstLock || !publicClient) {
      setDurations(null);
      return;
    }

    let cancelled = false;
    const fetchDurations = async () => {
      try {
        const [cliffDuration, vestingDuration, monthlyDuration, trancheCount] = await Promise.all([
          publicClient.readContract({
            address: firstLock,
            abi: TOKEN_LOCK_ABI,
            functionName: 'CLIFF_DURATION',
          }) as Promise<bigint>,
          publicClient.readContract({
            address: firstLock,
            abi: TOKEN_LOCK_ABI,
            functionName: 'VESTING_DURATION',
          }) as Promise<bigint>,
          publicClient.readContract({
            address: firstLock,
            abi: TOKEN_LOCK_ABI,
            functionName: 'MONTHLY_DURATION',
          }) as Promise<bigint>,
          publicClient.readContract({
            address: firstLock,
            abi: TOKEN_LOCK_ABI,
            functionName: 'TRANCHE_COUNT',
          }) as Promise<bigint>,
        ]);
        if (cancelled) return;
        setDurations({ cliffDuration, vestingDuration, monthlyDuration, trancheCount });
      } catch (err) {
        if (!cancelled) console.error('Error fetching duration constants:', err);
      }
    };

    fetchDurations();
    return () => { cancelled = true; };
  }, [firstLock, publicClient]);

  // ------------------------------------------------------------------
  // The grants themselves.
  // ------------------------------------------------------------------
  useEffect(() => {
    if (lockAddresses.length === 0 || !publicClient) {
      setLocks([]);
      setIsInitialized(false);
      return;
    }

    let cancelled = false;
    const fetchLocks = async () => {
      try {
        setIsLoading(true);
        const version = environment.factoryAbiVersion;
        const states = await Promise.all(
          lockAddresses.map((lock) => readLockState(publicClient, lock, version)),
        );
        if (cancelled) return;
        setLocks(states);
        // "Armed" is per grant. The wallet-level flag stays useful only as
        // "at least one of my grants is armed".
        setIsInitialized(states.some((state) => state.isInitialized));
      } catch (err) {
        if (!cancelled) {
          console.error('Error fetching lock info:', err);
          setError('Failed to fetch vesting information');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchLocks();

    // Refresh every 30 seconds
    const interval = setInterval(fetchLocks, 30000);
    return () => { cancelled = true; clearInterval(interval); };
    // lockKey, not lockAddresses: see the comment on lockKey.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lockKey, publicClient]);

  /** Look a grant up by address. Returns null for an address this wallet does not hold. */
  const getLock = (lockAddress: Address | string | null | undefined): LockInfo | null => {
    if (!lockAddress) return null;
    const index = lockAddresses.findIndex(
      (candidate) => candidate.toLowerCase() === String(lockAddress).toLowerCase(),
    );
    return index === -1 ? null : locks[index] ?? null;
  };

  /** The label for a grant, by address. Empty string on v7/v9 and for a redacted name. */
  const getLockName = (lockAddress: Address | string | null | undefined): string => {
    if (!lockAddress) return '';
    const index = lockAddresses.findIndex(
      (candidate) => candidate.toLowerCase() === String(lockAddress).toLowerCase(),
    );
    return index === -1 ? '' : lockNames[index] ?? '';
  };

  /**
   * Claim from ONE grant. The address is required and is not defaulted: with
   * several grants per wallet there is no "the" lock, and guessing one would
   * silently claim from the wrong position.
   */
  const claimTokens = async (lockAddress: Address) => {
    if (!walletClient) {
      throw new Error('Wallet not connected');
    }
    if (!lockAddresses.some((candidate) => candidate.toLowerCase() === lockAddress.toLowerCase())) {
      throw new Error('That lock does not belong to this wallet.');
    }

    try {
      setIsLoading(true);
      setIsClaiming(true);
      setError(null);

      const hash = await walletClient.writeContract({
        address: lockAddress,
        abi: TOKEN_LOCK_ABI,
        functionName: 'claim',
        args: [],
      });

      // Wait for transaction confirmation. viem resolves for a reverted receipt
      // rather than throwing, so a failed claim would otherwise reach the caller
      // as a success and refresh into unchanged balances.
      const receipt = await publicClient?.waitForTransactionReceipt({ hash });
      if (receipt && receipt.status === 'reverted') {
        throw new Error('The claim transaction reverted on-chain. No tokens were transferred.');
      }

      // Refresh just the grant that was claimed from.
      if (publicClient) {
        const info = await readLockState(publicClient, lockAddress, environment.factoryAbiVersion);
        setLocks((prev) => prev.map((lock, i) =>
          lockAddresses[i]?.toLowerCase() === lockAddress.toLowerCase() ? info : lock));
      }
    } catch (err: any) {
      console.error('Error claiming tokens:', err);
      const parsed = parseTxError(err, 'Failed to claim tokens.');
      // User cancellations shouldn't surface as a persistent error banner.
      setError(parsed.kind === 'rejected' ? null : parsed.message);
      throw err;
    } finally {
      setIsLoading(false);
      setIsClaiming(false);
    }
  };

  /**
   * The tranche table for ONE grant. Takes the grant explicitly for the same
   * reason claimTokens does: a wallet holding two positions has two schedules
   * and they share nothing but the duration constants.
   */
  const calculateVestingSchedule = (lockInfo: LockInfo | null): VestingSchedule[] => {
    if (!lockInfo || !durations) return [];

    const monthlyDurationSeconds = Number(durations.monthlyDuration);
    // TRANCHE_COUNT comes straight from the contract rather than being inferred
    // from the duration constants: the unlock curve no longer spans
    // VESTING_DURATION - CLIFF_DURATION, so that arithmetic would only guess.
    const totalMonths = Number(durations.trancheCount);
    if (monthlyDurationSeconds <= 0 || totalMonths <= 0) return [];

    const CLIFF_PERCENTAGE = 25;
    const MONTHLY_PERCENTAGE = 75 / totalMonths;

    // The track's unlock cliff is a fixed calendar date set by the owner on the
    // factory, so it IS the first tranche -- nothing is added to it.
    const cliffTime = Number(lockInfo.unlockCliffTime);
    if (cliffTime === 0) return []; // this track has no unlock date yet
    const totalAmount = lockInfo.totalAmount;
    const now = Math.floor(Date.now() / 1000);

    // Two figures decide what each tranche IS, and neither is a flag:
    //
    //   ceiling  — the most this grant can ever pay in total. A tranche whose
    //              cumulative sits above it will never pay, whatever its date.
    //   claimed  — what the holder has already been handed. A tranche whose
    //              cumulative sits at or below it has already been paid.
    //
    // This used to key off `cancelled || offboardEffective` instead, which is
    // wrong in both directions. A downward resize sets NEITHER flag and still
    // drops the ceiling — a grant cut below what it had already paid out went on
    // rendering every remaining tranche as an upcoming payment to a holder who
    // will never receive another token. And on a cancelled grant the flag marked
    // tranches forfeited that had in fact been claimed years earlier, which
    // misdescribes tokens already in the holder's wallet.
    //
    // payableCeiling is used rather than maxEntitlement directly because it is
    // the one place that knows a v7/v9 factory reports no ceiling at all, and
    // that a future-dated off-board has not bitten yet — TokenLock's own
    // `currentTime > offboardTime` guard.
    const ceiling = payableCeiling(lockInfo, Date.now());
    const claimed = lockInfo.unlockedAmount;

    // The contract releases min(vested, unlocked), so a tranche is only claimable
    // once the unlock date has passed AND vesting covers it. vestedAmount comes
    // straight from calculateVestedAmount, which already freezes at offboardTime
    // and collapses to zero on a cancellation, so it is what gates whether a
    // tranche has vested YET. What can never vest is decided by `ceiling` above,
    // not here: after a downward resize vestedAmount is simply small again, and
    // small is not the same as final.
    const vestedNow = lockInfo.vestedAmount;

    const schedule: VestingSchedule[] = [];
    const cliffAmount = totalAmount / 4n;
    schedule.push({
      date: new Date(cliffTime * 1000),
      percentage: CLIFF_PERCENTAGE,
      amount: cliffAmount,
      unlocked: now >= cliffTime,
      // A settled tranche was paid, so vesting must have covered it when it was
      // — even if a later resize has since pulled the live curve back below it.
      vested: cliffAmount <= claimed || cliffAmount <= vestedNow,
      settled: claimed > 0n && cliffAmount <= claimed,
      forfeited: cliffAmount > claimed && cliffAmount > ceiling,
    });

    const monthlyAmount = ((totalAmount * 75n) / 100n) / BigInt(totalMonths);
    let cumulativeUnlocked = cliffAmount;
    for (let i = 1; i <= totalMonths; i++) {
      // Only the cliff is pinned to the calendar; tranches run every 30 days
      // from it, so they drift off its day of the month by design.
      const unlockTime = cliffTime + i * monthlyDurationSeconds;
      cumulativeUnlocked += monthlyAmount;
      schedule.push({
        date: new Date(unlockTime * 1000),
        percentage: MONTHLY_PERCENTAGE,
        amount: monthlyAmount,
        unlocked: now >= unlockTime,
        vested: cumulativeUnlocked <= claimed || cumulativeUnlocked <= vestedNow,
        settled: claimed > 0n && cumulativeUnlocked <= claimed,
        forfeited: cumulativeUnlocked > claimed && cumulativeUnlocked > ceiling,
      });
    }

    return schedule;
  };

  // Totals across every grant this wallet holds, for a header that has to speak
  // about the wallet rather than about one position.
  const totals = useMemo(() => locks.reduce(
    (acc, lock) => ({
      totalAmount: acc.totalAmount + lock.totalAmount,
      unlockedAmount: acc.unlockedAmount + lock.unlockedAmount,
      availableAmount: acc.availableAmount + lock.availableAmount,
      earnedUnclaimed: acc.earnedUnclaimed + lock.earnedUnclaimed,
      // maxEntitlement, not totalAmount: a cancelled or off-boarded grant must
      // not be counted at its original size.
      maxEntitlement: acc.maxEntitlement + lock.maxEntitlement,
    }),
    {
      totalAmount: 0n, unlockedAmount: 0n, availableAmount: 0n,
      earnedUnclaimed: 0n, maxEntitlement: 0n,
    },
  ), [locks]);

  return {
    // State
    locks,
    lockAddresses,
    lockNames,
    totals,
    isEligible,
    isInitialized,
    isLoading,
    rolesLoaded,
    isClaiming,
    error,
    isOwner,
    isPendingOwner,
    isPaused,
    durations,
    tgeTime,
    incrementalFunding,

    // Actions
    getLock,
    getLockName,
    claimTokens,
    calculateVestingSchedule,
    setError,
  };
}
